import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Chip,
  Link,
  Box,
  CircularProgress,
  Divider,
  Avatar,
  Alert,
  IconButton,
  Paper
} from '@mui/material';
import { api } from 'src/Api';
import palette from 'src/theme/palette';
import Iconify from 'src/components/Iconify';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';

const ProjectDetailModal = ({ open, onClose, projectId }) => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (open && projectId) {
      getProjectById();
    }
  }, [open, projectId]);

  function getProjectById() {
    setLoading(true);
    setError(null);

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const queryParams = new URLSearchParams();
    projectId && queryParams.append('id', projectId);

    const requestOptions = {
      method: 'GET',
      redirect: 'follow',
      headers: myHeaders
    };

    fetch(`${api.get_project_by_id}?${queryParams.toString()}`, requestOptions)
      .then((response) => {
        if (response.status === 200) {
          return response.text();
        } else {
          throw new Error('Failed to fetch project data');
        }
      })
      .then((result_) => {
        const result = JSON.parse(result_);
        setDetails(result?.data);
      })
      .catch((error) => {
        console.error('error', error);
        setError('Failed to load project details. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const getContractType = (id) => {
    if (id === 1) {
      return {
        label: 'Fixed Price',
        desc: 'You will set the contract price for the client',
        icon: 'ion:lock-closed-outline'
      };
    }
    return {
      label: 'Open Book',
      desc: 'Price will be determined by your actual costs plus markup/margin (includes Cost Plus and Time & Materials contracts)',
      icon: 'ion:book-outline'
    };
  };

  const contractType = details ? getContractType(details?.contract_type_id) : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const StatusChip = ({ status }) => {
    const statusConfig = {
      presale: {
        color: 'info',
        icon: 'mdi:cart-outline',
        label: 'Presale',
        description: 'Project is in presale phase'
      },
      open: {
        color: 'success',
        icon: 'mdi:lock-open-outline',
        label: 'Open',
        description: 'Project is currently active'
      },
      warranty: {
        color: 'warning',
        icon: 'mdi:shield-check-outline',
        label: 'Warranty',
        description: 'Project is under warranty period'
      },
      closed: {
        color: 'default',
        icon: 'mdi:lock-outline',
        label: 'Closed',
        description: 'Project has been completed and closed'
      }
    };

    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '');
    const config = statusConfig[normalizedStatus] || statusConfig.open;

    return (
      <Chip
        icon={<Icon icon={config.icon} style={{ fontSize: 15 }} />}
        label={config.label}
        color={config.color}
        variant="filled"
        title={config.description}
        sx={{
          fontFamily: 'poppinsMedium',
          fontSize: 12,
          height: 25,
          px: 1.2,
          borderRadius: '100px',
          '& .MuiChip-icon': {
            fontSize: '18px'
          }
        }}
      />
    );
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '60vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: palette.primary.main,
          height: 50
        }}
      >
        <Typography sx={{ fontSize: 20, fontFamily: 'poppinsBold', color: palette.primary.white }}>
          Project Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#fff' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2, position: 'relative', minHeight: '400px' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px'
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={getProjectById} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && details && (
          <Grid container spacing={2}>
            {/* Header Section */}
            <Grid item xs={12}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontSize: 18,
                      fontFamily: 'poppinsBold',
                      color: palette.primary.titledark
                    }}
                  >
                    {details?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {details?.prefix && (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontFamily: 'poppinsMedium',
                              color: palette.primary.titledark
                            }}
                          >
                            Prefix:
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily: 'poppinsMedium',
                              color: palette.primary.main
                            }}
                          >
                            {details?.prefix}
                          </Typography>
                        </Box>
                        <Box component="span">•</Box>
                      </>
                    )}
                    {details?.project_status && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: 'poppinsMedium',
                            color: palette.primary.titledark
                          }}
                        >
                          Status:
                        </Typography>
                        <StatusChip status={details?.project_status} />
                      </Box>
                    )}
                  </Box>
                </Box>
                {contractType && (
                  <Chip
                    icon={<Iconify icon={contractType?.icon} style={{ fontSize: 15 }} />}
                    label={contractType?.label}
                    variant="filled"
                    color="primary"
                    sx={{
                      fontFamily: 'poppinsMedium',
                      fontSize: 12,
                      height: 25,
                      px: 1.2,
                      borderRadius: '100px',
                      '& .MuiChip-icon': {
                        fontSize: '18px'
                      }
                    }}
                  />
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Basic Information Section */}
            {(details?.project_type_name || details?.contract_price || contractType) && (
              <>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontFamily: 'poppinsSemiBold',
                      color: palette.primary.main
                    }}
                  >
                    Basic Information
                  </Typography>
                </Grid>

                {details?.project_type_name && (
                  <Grid item xs={12} md={6} sx={{ mb: { xs: 0, md: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium',
                          color: palette.primary.titledark
                        }}
                      >
                        Type :
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontFamily: 'poppinsMedium',
                          color: palette.primary.main,
                          ml: 0.5
                        }}
                      >
                        {details?.project_type_name}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {details?.contract_price && (
                  <Grid item xs={12} md={6} sx={{ mb: { xs: 0, md: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium',
                          color: palette.primary.titledark
                        }}
                      >
                        Contract Price:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontFamily: 'poppinsMedium',
                          color: palette.primary.main,
                          ml: 0.5
                        }}
                      >
                        ${details?.contract_price?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {contractType && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: 'poppinsMedium',
                          color: palette.primary.titledark,
                          mb: 0.5
                        }}
                      >
                        Contract Details :
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsRegular',
                            color: palette.primary.titledark
                          }}
                        >
                          {contractType?.desc}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {/* Managers Section */}
            {details?.managers?.length > 0 && (
              <Grid item xs={12}>
                <Typography
                  //   sx={{ mb: 1, fontWeight: 600, color: palette.primary.main }}
                  sx={{
                    fontSize: 18,
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.main,
                    mb: 1
                  }}
                >
                  Project Managers
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {details.managers.map((m) => (
                    <Chip
                      key={m.id}
                      avatar={
                        <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                          {m.manager_name?.charAt(0)}
                        </Avatar>
                      }
                      label={m.manager_name}
                      variant="outlined"
                      size="small"
                      sx={{
                        fontFamily: 'poppinsMedium',
                        fontSize: 12,
                        height: 25,
                        borderRadius: '100px',
                        '& .MuiChip-icon': {
                          fontSize: '18px'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Location Section */}
            {(details?.address || details?.city || details?.state || details?.zip_code) && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.main,
                    mb: 0.5
                  }}
                >
                  Location
                </Typography>
                <Box sx={{ p: 1.5, border: `1px solid ${palette.grey[300]}`, borderRadius: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontFamily: 'poppinsRegular',
                      color: palette.primary.titledark
                    }}
                  >
                    {[details?.address, details?.city, details?.state, details?.zip_code]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Timeline Section */}
            {(details?.project_start_date ||
              details?.project_actual_start_date ||
              details?.project_completion_date ||
              details?.project_actual_completion_date) && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.main,
                    mb: 0.5
                  }}
                >
                  Timeline
                </Typography>

                <Grid container spacing={2}>
                  {details?.project_start_date && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Iconify
                          icon="solar:calendar-bold"
                          width={18}
                          sx={{ mr: 1, color: palette.primary.main }}
                        />
                        <Box sx={{ mt: -0.2 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: palette.primary.title,
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            Planned Start
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: palette.primary.titledark,
                              fontFamily: 'poppinsSemiBold'
                            }}
                          >
                            {formatDate(details?.project_start_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {details?.project_actual_start_date && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Iconify
                          icon="solar:calendar-bold"
                          width={18}
                          sx={{ mr: 1, color: palette.primary.main }}
                        />
                        <Box sx={{ mt: -0.2 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: palette.primary.title,
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            Actual Start
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: palette.primary.titledark,
                              fontFamily: 'poppinsSemiBold'
                            }}
                          >
                            {formatDate(details?.project_actual_start_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {details?.project_completion_date && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Iconify
                          icon="solar:calendar-bold"
                          width={18}
                          sx={{ mr: 1, color: palette.primary.main }}
                        />
                        <Box sx={{ mt: -0.2 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: palette.primary.title,
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            Planned Completion
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: palette.primary.titledark,
                              fontFamily: 'poppinsSemiBold'
                            }}
                          >
                            {formatDate(details?.project_completion_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {details?.project_actual_completion_date && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Iconify
                          icon="solar:calendar-bold"
                          width={18}
                          sx={{ mr: 1, color: palette.primary.main }}
                        />
                        <Box sx={{ mt: -0.2 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: palette.primary.title,
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            Actual Completion
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: palette.primary.titledark,
                              fontFamily: 'poppinsSemiBold'
                            }}
                          >
                            {formatDate(details?.project_actual_completion_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Project Attributes Section */}
            {(details?.project_group?.length > 0 || details?.day?.length > 0 || details?.color) && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.main,
                    mb: 0.5
                  }}
                >
                  Project Attributes
                </Typography>

                <Grid container spacing={2}>
                  {details?.project_group?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Groups
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {details.project_group.map((g) => (
                            <Chip
                              key={g.id}
                              label={g.name}
                              size="small"
                              sx={{
                                fontFamily: 'poppinsMedium',
                                fontSize: 12,
                                height: 25,
                                px: 1.2,
                                borderRadius: '100px',
                                '& .MuiChip-icon': {
                                  fontSize: '18px'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {details?.day?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Work Days
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {details.day.map((d) => (
                            <Chip
                              key={d.id}
                              label={d.name}
                              size="small"
                              sx={{
                                fontFamily: 'poppinsMedium',
                                fontSize: 12,
                                height: 25,
                                px: 1.2,
                                borderRadius: '100px',
                                '& .MuiChip-icon': {
                                  fontSize: '18px'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {details?.color && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mr: 1
                          }}
                        >
                          Schedule Color:
                        </Typography>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: details?.color?.code || palette.grey[300]
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Notes Section */}
            {(details?.notes_for_internal_users || details?.notes_for_sub_vendor) && (
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontFamily: 'poppinsSemiBold',
                    color: palette.primary.main,
                    mb: 0.5
                  }}
                >
                  Notes
                </Typography>

                <Grid container spacing={2}>
                  {details?.notes_for_internal_users && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: palette.primary.title,
                          fontFamily: 'poppinsMedium',
                          mb: 0.5
                        }}
                      >
                        Internal Notes
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, minHeight: 100, backgroundColor: palette.grey[50] }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsRegular',
                            color: palette.primary.titledark
                          }}
                        >
                          {details?.notes_for_internal_users}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {details?.notes_for_sub_vendor && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: palette.primary.title,
                          fontFamily: 'poppinsMedium',
                          mb: 0.5
                        }}
                      >
                        Notes for Subs/Vendors
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, minHeight: 100, backgroundColor: palette.grey[50] }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsRegular',
                            color: palette.primary.titledark
                          }}
                        >
                          {details?.notes_for_sub_vendor}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Custom Fields Section */}
            {(details?.claim_information ||
              details?.permit_parcel_id ||
              details?.contractor_license ||
              details?.property_special_notes ||
              details?.policy_documents?.length > 0 ||
              details?.fl_gis_links ||
              details?.local_supplier_link) && (
              <>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontFamily: 'poppinsSemiBold',
                      color: palette.primary.main,
                      mb: 0.5
                    }}
                  >
                    Custom Fields
                  </Typography>

                  <Grid container spacing={2}>
                    {details?.claim_information && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Internal Notes
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, minHeight: 100, backgroundColor: palette.grey[50] }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily: 'poppinsRegular',
                              color: palette.primary.titledark
                            }}
                          >
                            {details?.claim_information}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {details?.permit_parcel_id && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Permit Parcel ID
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, minHeight: 50, backgroundColor: palette.grey[50] }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily: 'poppinsRegular',
                              color: palette.primary.titledark
                            }}
                          >
                            {details?.permit_parcel_id}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {details?.contractor_license && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Contractor License
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, minHeight: 100, backgroundColor: palette.grey[50] }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily: 'poppinsRegular',
                              color: palette.primary.titledark
                            }}
                          >
                            {details?.contractor_license}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {details?.property_special_notes && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Property Special Notes
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, minHeight: 100, backgroundColor: palette.grey[50] }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontFamily: 'poppinsRegular',
                              color: palette.primary.titledark
                            }}
                          >
                            {details?.property_special_notes}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {details?.policy_documents?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Policy Documents
                        </Typography>
                        <Box>
                          {details.policy_documents.map((doc) => (
                            <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Iconify
                                icon="stash:link-duotone"
                                sx={{ fontSize: 20, mr: 1, color: palette.primary.main }}
                              />
                              <Link
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener"
                                underline="hover"
                                sx={{
                                  fontSize: 13,
                                  fontFamily: 'poppinsRegular',
                                  color: palette.primary.main
                                }}
                              >
                                {doc.file_name}
                              </Link>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {details?.fl_gis_links && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          FL GIS Link
                        </Typography>
                        <Link
                          href={details.fl_gis_links}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsRegular',
                            color: palette.primary.main
                          }}
                        >
                          {details.fl_gis_links}
                        </Link>
                      </Grid>
                    )}

                    {details?.local_supplier_link && (
                      <Grid item xs={12} md={6}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: palette.primary.title,
                            fontFamily: 'poppinsMedium',
                            mb: 0.5
                          }}
                        >
                          Local Supplier Link
                        </Typography>
                        <Link
                          href={details.local_supplier_link}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{
                            fontSize: 13,
                            fontFamily: 'poppinsRegular',
                            color: palette.primary.main
                          }}
                        >
                          {details.local_supplier_link}
                        </Link>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </>
            )}

            <Grid
              item
              xs={12}
              sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mt: 4 }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: palette.primary.main,
                  fontStyle: 'italic',
                  fontFamily: 'poppinsSemiBold',
                  fontSize: 15,
                  textAlign: 'center'
                }}
              >
                — Detail End —
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      {/* <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            boxShadow: 'none'
          }}
        >
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default ProjectDetailModal;
